import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { speciesService } from '../services/speciesService';

interface UseGnnDiscoveryOptions {
  /** Polling interval in ms (default 5 minutes) */
  intervalMs?: number;
  /** Pairs per batch (default 10, max 20) */
  batchSize?: number;
  /** Enable/disable (default true) */
  enabled?: boolean;
  /** Context: companion or rotation */
  context?: 'companion' | 'rotation';
}

/**
 * Background GNN discovery hook.
 * Periodically finds uncovered species pairs, runs them through GNN,
 * and shows clickable toast notifications for interesting discoveries.
 */
export const useGnnDiscovery = ({
  intervalMs = 5 * 60 * 1000,
  batchSize = 10,
  enabled = true,
  context = 'companion',
}: UseGnnDiscoveryOptions = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const runningRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const allCoveredRef = useRef(false);

  const runDiscovery = useCallback(async () => {
    // Skip if already running, disabled, or all pairs covered
    if (runningRef.current || allCoveredRef.current) return;
    runningRef.current = true;

    try {
      const result = await speciesService.discoverNewEdges(context, batchSize);

      if (result.analyzed === 0 && result.remaining === 0) {
        // All pairs covered — stop polling
        allCoveredRef.current = true;
        return;
      }

      if (result.discoveries && result.discoveries.length > 0) {
        // Invalidate companion spectrum + alerts caches
        queryClient.invalidateQueries({ queryKey: ['companion-spectrum'] });
        queryClient.invalidateQueries({ queryKey: ['gnn-predictions'] });
        queryClient.invalidateQueries({ queryKey: ['alerts'] });
        queryClient.invalidateQueries({ queryKey: ['alert-stats'] });

        // Show clickable toasts (max 3)
        const toShow = result.discoveries.slice(0, 3);
        for (const d of toShow) {
          const netScore = d.net_score ?? (Number(d.predicted_benefit || 0) - Number(d.predicted_harm || 0));
          const isGood = netScore > 0;

          toast(
            (t) => (
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate(`/plant-o-pedia/species/${d.species_id_a}?tab=health`);
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: '6px' }}>
                   <span style={{ fontSize: '1.2em' }}>🧠</span> G-Root — {isGood ? 'Dobrá vazba' : 'Riziková vazba'}
                </div>
                <div style={{ fontSize: '0.85em', opacity: 0.85 }}>
                  {d.species_a_name} + {d.species_b_name}{' '}
                  <span style={{ fontWeight: 700, color: isGood ? '#22c55e' : '#ef4444' }}>
                    ({netScore > 0 ? '+' : ''}{Number(netScore).toFixed(1)})
                  </span>
                </div>
                <div style={{ fontSize: '0.75em', opacity: 0.5, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Klikni pro detail</span>
                  <span style={{ fontStyle: 'italic' }}>AI Analýza</span>
                </div>
              </div>
            ),
            {
              duration: 8000,
              style: {
                borderLeft: `4px solid ${isGood ? '#22c55e' : '#ef4444'}`,
                padding: '12px 16px',
                borderRadius: '12px',
                background: '#fff',
                color: '#1f2937',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              },
            },
          );
        }

        if (result.discoveries.length > 3) {
          toast(
            (t) => (
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate('/notifications');
                }}
              >
                🧠 G-Root: ...a {result.discoveries.length - 3} dalších nálezů
                <div style={{ fontSize: '0.75em', opacity: 0.5 }}>Klikni pro přehled</div>
              </div>
            ),
            { duration: 5000 },
          );
        }
      }
    } catch {
      // Silently ignore — background task, don't bother user
    } finally {
      runningRef.current = false;
    }
  }, [context, batchSize, queryClient, navigate]);

  useEffect(() => {
    if (!enabled) return;

    // Reset coverage flag on enable/context change
    allCoveredRef.current = false;

    // Initial run after short delay (let page settle)
    const initialTimer = setTimeout(runDiscovery, 15_000);

    // Periodic polling
    timerRef.current = setInterval(runDiscovery, intervalMs);

    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled, intervalMs, runDiscovery]);

  return {
    /** Manually trigger a discovery run */
    triggerNow: runDiscovery,
    /** Whether all pairs have been covered */
    allCovered: allCoveredRef.current,
  };
};
